"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ChevronDown, Search, MoreVertical, Save, ChevronRight, ChevronUp, Settings, Columns, Zap } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useTranslation } from '@/hooks/use-translation'
import { useToast } from '@/hooks/use-toast'
import type { SavedView } from '@/types/saved-view'
import { ManageViewsModal } from './manage-views-modal'
import { AutomationModal } from './automation-modal'
import { env } from '@/lib/env'
import { WindowRefService } from '@/lib/window-ref-service'
import { useFeatureFlags } from '@/contexts/feature-flag-context'

// Reusable SaveViewModal component
interface SaveViewModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (title: string, isPublic: boolean, saveFilters: boolean) => void
  initialTitle?: string
  initialIsPublic?: boolean
  isSaveAs?: boolean
  isEdit?: boolean
}

export function SaveViewModal({ isOpen, onClose, onSave, initialTitle = "", initialIsPublic = false, isSaveAs = false, isEdit = false }: SaveViewModalProps) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(initialTitle)
  const [isPublic, setIsPublic] = useState(false)
  const { currentView } = useWorkflow()
  
  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle)
      setIsPublic(isEdit ? initialIsPublic : false)
    }
  }, [isOpen, initialTitle, initialIsPublic, isEdit])

  const handleSave = (saveFilters: boolean) => {
    if (title.trim()) {
      onSave(title.trim(), isPublic, saveFilters)
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit view" : (isSaveAs ? (t('saveViewAs') || "Save view as") : (t('saveView') || "Save view"))}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="title" className="text-sm font-medium">
              {t('title') || "Title"}
            </label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('myCustomView') || "My custom view"}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">
              {t('visibility') || "Visibility"}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="private"
                name="visibility"
                checked={!isPublic}
                onChange={() => setIsPublic(false)}
                className="mr-2"
              />
              <label htmlFor="private" className="text-sm">
                {t('private') || "Private"}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="public"
                name="visibility"
                checked={isPublic}
                onChange={() => setIsPublic(true)}
                className="mr-2"
              />
              <label htmlFor="public" className="text-sm">
                {t('public') || "Public"}
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="elliptical-outline" onClick={onClose}>
            {t('cancel') || "Cancel"}
          </Button>
          <Button variant="elliptical" onClick={() => handleSave(isSaveAs)} disabled={!title.trim()}>
            {t('save') || "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ViewSelector() {
  const { currentView, setCurrentView, saveCurrentView, saveViewAs, deleteView, hasUnsavedChanges, viewsResponse, updateViewMetadata, configuration, stageFilters, globalFilters, currentUser } =
    useWorkflow()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { trackViewAction } = useWorkflowTracking()
  const { isFeatureEnabled } = useFeatureFlags()

  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false)
  const [isSaveAsDialogOpen, setIsSaveAsDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [newViewName, setNewViewName] = useState("")
  const [newViewIsPublic, setNewViewIsPublic] = useState(false)
  
  const searchInputRef = useRef<HTMLInputElement>(null)

  // State for the three dots menu
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [isSaveSubmenuOpen, setIsSaveSubmenuOpen] = useState(false)
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false)
  const [saveViewMode, setSaveViewMode] = useState<'save' | 'saveAs' | 'edit'>('save')
  
  // State for manage views modal
  const [isManageViewsModalOpen, setIsManageViewsModalOpen] = useState(false)
  
  // State for automation modal
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false)
  
  // State for unsaved changes confirmation dialog
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false)
  const [pendingViewSwitch, setPendingViewSwitch] = useState<SavedView | null>(null)
  
  // State for private column warning dialog
  const [isPrivateColumnWarningOpen, setIsPrivateColumnWarningOpen] = useState(false)
  const [pendingSaveData, setPendingSaveData] = useState<{title: string, isPublic: boolean, saveFilters: boolean} | null>(null)

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<{
    myViews: boolean
    sharedWithMe: boolean
    allCompany: boolean
  }>({
    myViews: false,
    sharedWithMe: false,
    allCompany: false
  })

  // State for showing more views - track how many items to show for each section
  const [visibleCounts, setVisibleCounts] = useState<{
    myViews: number
    sharedWithMe: number
    allCompany: number
  }>({
    myViews: 3,
    sharedWithMe: 3,
    allCompany: 3
  })

  // Get the different view types from the original API response
  const defaultView = viewsResponse?.defaultView || null
  const userViews = viewsResponse?.userViews || []
  const sharedViews = viewsResponse?.sharedViews || []
  const publicViews = viewsResponse?.publicViews || []

  // Filter to only show visible views in the selector (but always show default view)
  const visibleUserViews = userViews.filter(view => view.isVisible !== false)
  const visibleSharedViews = sharedViews.filter(view => view.isVisible !== false)
  const visiblePublicViews = publicViews.filter(view => view.isVisible !== false)

  // Get authorized actions for permission checking
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasModifyDefaultViewPermission = authorizedActions.includes("ModifyDefaultView")
  const hasModifyPublicViewsPermission = authorizedActions.includes("ModifyPublicViews")
  const hasManageViewsPermission = authorizedActions.includes("ManageViews")
  const hasAutomationsLinkPermission = authorizedActions.includes("AutomationsLink")
  const hasAdminPermission = authorizedActions.some((action: string) => 
    action.toLowerCase().includes("admin") || action.toLowerCase().includes("administrator")
  )

  // Function to check if public view has private column filters and user should be warned
  const shouldWarnAboutPrivateColumns = (isPublic: boolean): boolean => {

    if (!isPublic || !configuration) return false
    // Get all current filters (both stage and global filters)
    const allFilters = [...stageFilters, ...globalFilters]
    if (allFilters.length === 0) return false
    
    // Get field configurations
    const fieldConfigurations = configuration.fieldsInUse || []
    
    // Check if there's at least one private column with filters applied
    const hasPrivateColumnFilter = allFilters.some((filter) => {
      const fieldConfig = fieldConfigurations.find((fc) => fc.fieldConfigurationId === filter.fieldConfigurationId)
      return fieldConfig?.isPrivate === true
    })
    if (!hasPrivateColumnFilter) return false
    
    // Check user permissions
    const authorizedActions = (configuration.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
    const hasViewPrivateColumnsPermission = authorizedActions.includes("ViewPrivateColumns")

    // If user has ViewPrivateColumns permission, always show warning
    if (hasViewPrivateColumnsPermission) return true
    
    // Otherwise, check if user has access to at least one private column that has filters
    const hasAccessToPrivateColumns = allFilters.some((filter) => {
      const fieldConfig = fieldConfigurations.find((fc) => fc.fieldConfigurationId === filter.fieldConfigurationId)
      if (!fieldConfig || !fieldConfig.isPrivate) return false
      
      // Check if current user is in the authorizedUserList
      const authorizedUsers = fieldConfig.authorizedUserList || []
      return authorizedUsers.some((user: any) => user.email === currentUser?.email)
    })
    return hasAccessToPrivateColumns
  }

  // Check if current view can be saved
  const canSaveCurrentView = currentView && configuration && (() => {
    // Check if this is the default view (either explicitly marked as isDefault or matches defaultView from API)
    const isDefaultView = currentView.isDefault === true || 
                         (defaultView?.viewSettingsId === currentView.viewSettingsId)
    
    // For default views: user must have ModifyDefaultView OR admin permission
    if (isDefaultView) {
      return hasModifyDefaultViewPermission || hasAdminPermission
    }
    
    // Check if user owns this view (in user views)
    const isOwner = userViews.some(view => view.viewSettingsId === currentView.viewSettingsId)
    if (isOwner) {
      return true
    }
    
    // Check if it's a public view and user has ModifyPublicViews permission
    const isPublicView = publicViews.some(view => view.viewSettingsId === currentView.viewSettingsId)
    if (isPublicView) {
      return hasModifyPublicViewsPermission || hasAdminPermission
    }
    
    // Check if it's a shared view (user can save if they're admin)
    const isSharedView = sharedViews.some(view => view.viewSettingsId === currentView.viewSettingsId)
    if (isSharedView) {
      return hasAdminPermission
    }
    
    // Fallback: admin can always save
    return hasAdminPermission
  })()

  // Filter views based on search term
  const filterViewsBySearch = (views: SavedView[]) => {
    if (!searchTerm.trim()) return views
    return views.filter((view) => 
      view.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  // Always show default view regardless of isVisible status
  const filteredDefaultView = defaultView ? (filterViewsBySearch([defaultView])[0] || defaultView) : null
  const filteredUserViews = filterViewsBySearch(visibleUserViews)
  const filteredSharedViews = filterViewsBySearch(visibleSharedViews)
  const filteredPublicViews = filterViewsBySearch(visiblePublicViews)

  // Get limited views for display
  const getLimitedViews = (views: SavedView[], section: 'myViews' | 'sharedWithMe' | 'allCompany') => {
    // Always show all views when searching
    if (searchTerm.trim()) {
      return views
    }
    // Show up to the visible count for this section
    return views.slice(0, visibleCounts[section])
  }

  const limitedUserViews = getLimitedViews(filteredUserViews, 'myViews')
  const limitedSharedViews = getLimitedViews(filteredSharedViews, 'sharedWithMe')
  const limitedPublicViews = getLimitedViews(filteredPublicViews, 'allCompany')

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  // Handle view switching with unsaved changes confirmation
  const handleViewSwitch = async (view: SavedView) => {
    if (currentView?.viewSettingsId !== view.viewSettingsId) {
      // Check if user has unsaved changes and can modify current view and has ManageViews permission
      if (hasUnsavedChanges && canSaveCurrentView && hasManageViewsPermission) {
        // Show confirmation dialog
        setPendingViewSwitch(view)
        setIsUnsavedChangesDialogOpen(true)
      } else {
        // Switch view immediately
        trackViewAction('load', view.title)
        await setCurrentView(view)
        setIsViewDropdownOpen(false)
      }
    }
  }

  // Handle confirmation to switch view and lose unsaved changes
  const handleConfirmViewSwitch = async () => {
    if (pendingViewSwitch) {
      trackViewAction('load', pendingViewSwitch.title)
      await setCurrentView(pendingViewSwitch)
      setIsViewDropdownOpen(false)
      setIsUnsavedChangesDialogOpen(false)
      setPendingViewSwitch(null)
    }
  }

  // Handle cancel view switch
  const handleCancelViewSwitch = () => {
    setIsUnsavedChangesDialogOpen(false)
    setPendingViewSwitch(null)
  }

  // Maintain focus on search input when dropdown is open
  useEffect(() => {
    if (isViewDropdownOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [isViewDropdownOpen])

  // Reset show more state when dropdown closes
  useEffect(() => {
    if (!isViewDropdownOpen) {
      setVisibleCounts({
        myViews: 3,
        sharedWithMe: 3,
        allCompany: 3
      })
    }
  }, [isViewDropdownOpen])

  // Cleanup pointer-events when all dialogs/menus are closed
  useEffect(() => {
    const allClosed = !isActionsMenuOpen && !isSaveSubmenuOpen && 
                     !isSaveViewModalOpen && !isPrivateColumnWarningOpen && 
                     !isDeleteDialogOpen && !isManageViewsModalOpen && 
                     !isAutomationModalOpen && !isUnsavedChangesDialogOpen
    
    if (allClosed) {
      // Ensure body pointer-events are reset
      const timer = setTimeout(() => {
        if (document.body.style.pointerEvents === 'none') {
          document.body.style.pointerEvents = ''
        }
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [isActionsMenuOpen, isSaveSubmenuOpen, isSaveViewModalOpen, 
      isPrivateColumnWarningOpen, isDeleteDialogOpen, isManageViewsModalOpen, 
      isAutomationModalOpen, isUnsavedChangesDialogOpen])

  // Toggle section collapse
  const toggleSection = (section: 'myViews' | 'sharedWithMe' | 'allCompany') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Handle save as (original function)
  const handleSaveAsOriginal = async () => {
    if (newViewName.trim()) {
      try {
        await saveViewAs(newViewName.trim(), newViewIsPublic)
        setNewViewName("")
        setNewViewIsPublic(false)
        setIsSaveAsDialogOpen(false)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('saveView') || "View saved successfully",
        })
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('error') || "Error",
          description: error instanceof Error ? error.message : "Failed to save view",
        })
      }
    }
  }

  // Handle delete (original function)
  const handleDeleteOriginal = async () => {
    if (currentView) {
      try {
        await deleteView(currentView.viewSettingsId)
        setIsDeleteDialogOpen(false)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('deleteView') || "View deleted successfully",
        })
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('error') || "Error",
          description: error instanceof Error ? error.message : "Failed to delete view",
        })
      }
    }
  }

  const handleSave = async () => {
    // Close menus first
    setIsActionsMenuOpen(false)
    setIsSaveSubmenuOpen(false)
    
    // Check if we need to show private column warning for public views
    const isCurrentViewPublic = currentView?.isPublic === true
    if (shouldWarnAboutPrivateColumns(isCurrentViewPublic)) {
      setPendingSaveData({ title: currentView?.title || '', isPublic: isCurrentViewPublic, saveFilters: false })
      // Delay opening dialog to allow dropdown cleanup
      setTimeout(() => {
        setIsPrivateColumnWarningOpen(true)
      }, 100)
      return
    }

    // No warning needed, proceed with save
    await handleSaveInternal()
  }

  const handleSaveInternal = async () => {
    try {
      await saveCurrentView()
      trackViewAction('save', currentView?.title || 'Custom View')
      toast({
        variant: "success",
        title: t('success') || "Success",
        description: t('changesSuccess') || "Changes saved successfully",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to save changes",
      })
    }
  }

  const handleEdit = () => {
    trackViewAction('edit', currentView?.title || 'Custom View')
    setIsActionsMenuOpen(false)
    // Delay opening modal to allow dropdown cleanup
    setTimeout(() => {
      setIsManageViewsModalOpen(true)
    }, 100)
  }

  const handleSaveAs = () => {
    trackViewAction('create', 'New View')
    setSaveViewMode('saveAs')
    // Close menus first
    setIsActionsMenuOpen(false)
    setIsSaveSubmenuOpen(false)
    // Delay opening modal to allow dropdown cleanup
    setTimeout(() => {
      setIsSaveViewModalOpen(true)
    }, 100)
  }

  const handleDelete = () => {
    trackViewAction('delete', currentView?.title || 'Custom View')
    setIsActionsMenuOpen(false)
    // Delay opening dialog to allow dropdown cleanup
    setTimeout(() => {
      setIsDeleteDialogOpen(true)
    }, 100)
  }

  const handleSaveViewModalSave = async (title: string, isPublic: boolean, saveFilters: boolean) => {
    // Check if we need to show private column warning
    if (shouldWarnAboutPrivateColumns(isPublic)) {
      setPendingSaveData({ title, isPublic, saveFilters })
      setIsSaveViewModalOpen(false)
      // Delay opening dialog to allow previous dialog cleanup
      setTimeout(() => {
        setIsPrivateColumnWarningOpen(true)
      }, 100)
      return
    }
    
    // No warning needed, proceed with save
    await handleSaveViewModalSaveInternal(title, isPublic, saveFilters)
  }

  const handleSaveViewModalSaveInternal = async (title: string, isPublic: boolean, saveFilters: boolean) => {
    try {
      if (saveViewMode === 'save') {
        await saveCurrentView()
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('changesSuccess') || "Changes saved successfully",
        })
      } else if (saveViewMode === 'edit') {
        // For edit mode, we need to update the current view's title and isPublic
        await updateViewMetadata(title, isPublic, true, false)
        trackViewAction('edit', title)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: "View updated successfully",
        })
      } else {
        const newView = await saveViewAs(title, isPublic) // Pass isPublic from modal
        if (newView) {
          await setCurrentView(newView)
        }
        trackViewAction('create', title)
        toast({
          variant: "success",
          title: t('success') || "Success",
          description: t('saveViewAs') || "View saved as new successfully",
        })
      }
      setIsSaveViewModalOpen(false)
    } catch (error) {
      console.error('Failed to save view:', error)
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: error instanceof Error ? error.message : "Failed to save view",
      })
    }
  }

  return (
    <div className="flex items-center gap-2">
      {/* View Selector Dropdown */}
      <div className="relative">
        <TooltipProvider>
          <DropdownMenu open={isViewDropdownOpen} onOpenChange={setIsViewDropdownOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 min-w-[200px] justify-between">
                    <span className="truncate">{currentView?.title || "Default"}</span>
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('viewSelector') || "View selector"}</p>
              </TooltipContent>
            </Tooltip>
          <DropdownMenuContent className="w-[280px]" align="start">
            <div className="p-2">
              <div className="relative mb-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchInputRef}
                  placeholder={t('search') || "Search"}
                  className="pl-8"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Default view - no section title */}
            {filteredDefaultView && (
              <DropdownMenuItem
                onClick={() => handleViewSwitch(filteredDefaultView)}
                className={currentView?.viewSettingsId === filteredDefaultView.viewSettingsId ? "bg-gray-100" : ""}
              >
                {filteredDefaultView.title}
              </DropdownMenuItem>
            )}

            {/* My views section */}
            <DropdownMenuSeparator />
            <div 
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleSection('myViews')
              }}
              className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-500 cursor-pointer hover:bg-gray-50"
            >
              <span>{t('myViews') || "My views"}</span>
              {collapsedSections.myViews ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </div>
            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${collapsedSections.myViews ? 'max-h-0' : 'max-h-96'}`}>
              {limitedUserViews.map((view) => (
                <DropdownMenuItem
                  key={view.viewSettingsId}
                  onClick={() => handleViewSwitch(view)}
                  className={currentView?.viewSettingsId === view.viewSettingsId ? "bg-gray-100" : ""}
                >
                  {view.title}
                </DropdownMenuItem>
              ))}
              {filteredUserViews.length > visibleCounts.myViews && !searchTerm.trim() && (
                <div className="px-2 py-1.5">
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setVisibleCounts(prev => {
                        const newCount = Math.min(prev.myViews + 5, filteredUserViews.length)
                        return { ...prev, myViews: newCount }
                      })
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 w-full text-left"
                  >
                    {t('showMore') || "Show more"} ({filteredUserViews.length - visibleCounts.myViews})
                  </button>
                </div>
              )}
            </div>



            {/* All company section */}
            <DropdownMenuSeparator />
            <div 
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleSection('allCompany')
              }}
              className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-500 cursor-pointer hover:bg-gray-50"
            >
              <span>{t('allCompany') || "All company"}</span>
              {collapsedSections.allCompany ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </div>
            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${collapsedSections.allCompany ? 'max-h-0' : 'max-h-96'}`}>
              {limitedPublicViews.map((view) => (
                <DropdownMenuItem
                  key={view.viewSettingsId}
                  onClick={() => handleViewSwitch(view)}
                  className={currentView?.viewSettingsId === view.viewSettingsId ? "bg-gray-100" : ""}
                >
                  {view.title}
                </DropdownMenuItem>
              ))}
              {filteredPublicViews.length > visibleCounts.allCompany && !searchTerm.trim() && (
                <div className="px-2 py-1.5">
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setVisibleCounts(prev => {
                        const newCount = Math.min(prev.allCompany + 5, filteredPublicViews.length)
                        return { ...prev, allCompany: newCount }
                      })
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 w-full text-left"
                  >
                    {t('showMore') || "Show more"} ({filteredPublicViews.length - visibleCounts.allCompany})
                  </button>
                </div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        </TooltipProvider>
      </div>

      {/* More Options Button */}
      <DropdownMenu open={isActionsMenuOpen} onOpenChange={setIsActionsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVertical size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {hasManageViewsPermission && (
            <DropdownMenu open={isSaveSubmenuOpen} onOpenChange={setIsSaveSubmenuOpen}>
              <DropdownMenuTrigger asChild>
                <DropdownMenuItem 
                  onSelect={(e) => e.preventDefault()}
                  className="pl-2 pr-2"
                >
                  <Columns size={16} className="mr-2" />
                  {t('saveThisView') || "Save this view"}
                  <ChevronRight size={16} className="ml-auto" />
                </DropdownMenuItem>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" className="w-48" align="start">
                <DropdownMenuItem 
                  onClick={handleSave}
                  disabled={!canSaveCurrentView}
                  className="pl-2"
                >
                  <Save size={16} className="mr-2" />
                  {t('saveChanges') || "Save changes"}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSaveAs}
                  className="pl-2"
                >
                  <Save size={16} className="mr-2" />
                  {t('saveAsNew') || "Save as new"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenuItem onClick={handleEdit} className="pl-2 pr-2">
            <Settings size={16} className="mr-2" />
            {"Manage views"}
          </DropdownMenuItem>
          {isFeatureEnabled('automationsLinkEnabled') && hasAutomationsLinkPermission && (
            <DropdownMenuItem 
              onClick={() => {
                setIsActionsMenuOpen(false)
                // Delay opening modal to allow dropdown cleanup
                setTimeout(() => {
                  setIsAutomationModalOpen(true)
                }, 100)
              }} 
              className="pl-2 pr-2"
            >
              <Zap size={16} className="mr-2" />
              {t('automations') || "Automations"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save View Modal */}
      <SaveViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        onSave={handleSaveViewModalSave}
        initialTitle={saveViewMode === 'saveAs' && currentView?.title ? `${currentView.title} - Copy` : (currentView?.title || "")}
        initialIsPublic={currentView?.isPublic ?? false}
        isSaveAs={saveViewMode === 'saveAs'}
        isEdit={saveViewMode === 'edit'}
      />

      {/* Save As Dialog */}
      <Dialog open={isSaveAsDialogOpen} onOpenChange={setIsSaveAsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('saveViewAs') || "Save view as"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="name" className="text-sm font-medium">
                {t('title') || "Title"}
              </label>
              <Input
                id="name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder={t('myCustomView') || "My custom view"}
              />
            </div>
            
            <div className="grid gap-2">
              <label className="text-sm font-medium">
                {t('visibility') || "Visibility"}
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="old-private"
                  name="old-visibility"
                  checked={!newViewIsPublic}
                  onChange={() => setNewViewIsPublic(false)}
                  className="mr-2"
                />
                <label htmlFor="old-private" className="text-sm">
                  {t('private') || "Private"}
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="old-public"
                  name="old-visibility"
                  checked={newViewIsPublic}
                  onChange={() => setNewViewIsPublic(true)}
                  className="mr-2"
                />
                <label htmlFor="old-public" className="text-sm">
                  {t('public') || "Public"}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="elliptical-outline" onClick={() => setIsSaveAsDialogOpen(false)}>
              {t('cancel') || "Cancel"}
            </Button>
            <Button variant="elliptical" onClick={handleSaveAsOriginal} disabled={!newViewName.trim()}>
              {t('save') || "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteViewConfirmation') || "Are you sure you want to delete this view?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteViewWarning') || "This view will be deleted for everyone it has been shared with. You cannot undo this action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel') || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOriginal} className="bg-red-600 hover:bg-red-700">
              {t('delete') || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Views Modal */}
      <ManageViewsModal
        isOpen={isManageViewsModalOpen}
        onClose={() => setIsManageViewsModalOpen(false)}
      />

      {/* Automation Modal */}
      <AutomationModal
        isOpen={isAutomationModalOpen}
        onClose={() => setIsAutomationModalOpen(false)}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <AlertDialog open={isUnsavedChangesDialogOpen} onOpenChange={setIsUnsavedChangesDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('unsavedChangesTitle') || "Unsaved Changes"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('unsavedChangesWarning') || "You have unsaved changes to your filters. Switching views will discard these changes. Are you sure you want to continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelViewSwitch} variant="elliptical-outline">
              {t('cancel') || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmViewSwitch} variant="elliptical">
              {t('switchView') || "Switch View"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Private Column Warning Dialog */}
      <AlertDialog open={isPrivateColumnWarningOpen} onOpenChange={setIsPrivateColumnWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('privateColumnWarningTitle') || "Private Column Filters Warning"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('privateColumnWarningMessage') || "This public view contains filters on private columns. Other users who don't have access to these private columns may not see the same results or may encounter errors when using this view. Are you sure you want to save this public view?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setIsPrivateColumnWarningOpen(false)
                setPendingSaveData(null)
              }} 
              variant="elliptical-outline"
            >
              {t('cancel') || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                if (pendingSaveData) {
                  // Check if this is a modal save (has title different from current view) or a direct save
                  if (pendingSaveData.title !== currentView?.title || pendingSaveData.saveFilters) {
                    // Modal save - use the modal save handler
                    await handleSaveViewModalSaveInternal(pendingSaveData.title, pendingSaveData.isPublic, pendingSaveData.saveFilters)
                  } else {
                    // Direct save - use the internal save handler
                    await handleSaveInternal()
                  }
                }
                setIsPrivateColumnWarningOpen(false)
                setPendingSaveData(null)
              }} 
              variant="elliptical"
            >
              {t('saveAnyway') || "Save Anyway"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
